import get from 'lodash/get'
import { createReducer } from 'redux-create-reducer'
import type { EmailCampaign, EmailCampaignId } from 'types/campaign'
import {
  CAMPAIGN_BEGIN_SENDING,
  CAMPAIGN_BEGIN_SENDING_FAIL,
  CAMPAIGN_BEGIN_SENDING_SUCCESS,
  CAMPAIGN_DETAILS_MARK_DIRTY,
  CAMPAIGN_DETAILS_RESOLVE_CONFLICT,
  CAMPAIGN_DETAILS_SAVE,
  CAMPAIGN_DETAILS_SAVE_CONFLICT,
  CAMPAIGN_DETAILS_SAVE_FAIL,
  CAMPAIGN_DETAILS_SAVE_SUCCESS,
  CAMPAIGN_DETAILS_UPDATE,
  CAMPAIGN_LIST_CREATE_SUCCESS,
  CAMPAIGN_LIST_DELETE,
  CAMPAIGN_LIST_DELETE_FAIL,
  CAMPAIGN_LIST_DELETE_SUCCESS,
  CAMPAIGN_LIST_GET,
  CAMPAIGN_LIST_GET_ALL,
  CAMPAIGN_LIST_GET_ALL_FAIL,
  CAMPAIGN_LIST_GET_ALL_SUCCESS,
  CAMPAIGN_LIST_GET_FAIL,
  CAMPAIGN_LIST_GET_SUCCESS,
  CAMPAIGN_RESCHEDULED_SUCCESS,
  CAMPAIGN_SCHEDULE_CANCELLED_SUCCESS,
  CAMPAIGN_SCHEDULED_TO_SEND_SUCCESS,
  CAMPAIGN_STATS_LOAD,
  CAMPAIGN_STATS_LOAD_FAIL,
  CAMPAIGN_STATS_LOAD_SUCCESS,
} from '@disco/constants/ActionTypes'
export type EmailCampaignsState = {
  loading: boolean
  loaded: boolean
  error: Error | null | undefined
  emailCampaigns: Record<EmailCampaignId, EmailCampaign>
}
const initialState: EmailCampaignsState = {
  loading: false,
  loaded: false,
  error: null,
  emailCampaigns: {},
}

function update(
  state: EmailCampaignsState,
  id: EmailCampaignId,
  updated: Partial<EmailCampaign>,
): EmailCampaignsState {
  return {
    ...state,
    emailCampaigns: {
      ...state.emailCampaigns,
      [id]: {
        ...(state.emailCampaigns[id] || {}),
        ...updated,
      } as EmailCampaign,
    },
  }
}

export const emailCampaignsReducer = createReducer<EmailCampaignsState, any>(
  initialState,
  {
    [CAMPAIGN_LIST_GET_ALL](state) {
      return { ...state, loading: true, error: null }
    },

    [CAMPAIGN_LIST_GET_ALL_SUCCESS](state, { campaigns }) {
      const updatedEmailCampaigns = { ...state.emailCampaigns }
      campaigns.forEach(c => {
        const existing = updatedEmailCampaigns[c.id]
        // a list refresh must not clobber edits that have not been saved yet,
        // but status still decides whether saving is allowed, so the server wins there
        updatedEmailCampaigns[c.id] = existing?.dirty
          ? { ...c, ...existing, status: c.status }
          : c
      })
      return {
        ...state,
        loaded: true,
        loading: false,
        error: null,
        emailCampaigns: updatedEmailCampaigns,
      }
    },

    [CAMPAIGN_LIST_GET_ALL_FAIL](state, action) {
      return { ...state, loading: false, error: action.error }
    },

    [CAMPAIGN_LIST_GET](state, { campaignId }) {
      return update(state, campaignId, {
        loading: true,
        loadingError: undefined,
      })
    },

    [CAMPAIGN_LIST_GET_SUCCESS](state, { campaignId, campaign }) {
      return update(state, campaignId, { ...campaign, loading: false })
    },

    [CAMPAIGN_LIST_GET_FAIL](state, { error, campaignId }) {
      return update(state, campaignId, {
        loading: false,
        loadingError: error,
      })
    },

    [CAMPAIGN_LIST_CREATE_SUCCESS](state, action) {
      return update(state, action.campaign.id, action.campaign)
    },

    [CAMPAIGN_DETAILS_UPDATE](state, action) {
      return update(state, action.campaignId, action.updatedFields)
    },

    [CAMPAIGN_DETAILS_MARK_DIRTY](state, action) {
      return update(state, action.campaignId, {
        dirty: true,
      })
    },

    [CAMPAIGN_DETAILS_SAVE](state, action) {
      return update(state, action.campaignId, {
        saving: true,
      })
    },

    [CAMPAIGN_DETAILS_SAVE_SUCCESS](state, action) {
      return update(state, action.campaignId, {
        ...action.campaign,
        saving: false,
        dirty: false,
        savedAt: new Date(),
      })
    },

    [CAMPAIGN_DETAILS_SAVE_CONFLICT](state, action) {
      return update(state, action.campaignId, {
        conflicting: {
          newerSavedAt: action.newerSavedAt,
          newerCampaign: action.newerCampaign,
        },
      })
    },

    [CAMPAIGN_DETAILS_RESOLVE_CONFLICT](state, action) {
      if (action.option === 'CONFLICT_LOAD_LATEST') {
        const campaign = state.emailCampaigns[action.campaignId]
        if (!campaign?.conflicting) return state
        return update(state, action.campaignId, {
          ...campaign.conflicting.newerCampaign,
          conflicting: undefined,
          saving: false,
        })
      } else if (action.option === 'CONFLICT_CANCEL_SAVING') {
        return update(state, action.campaignId, {
          conflicting: undefined,
          saving: false,
        })
      } else {
        return state
      }
    },

    [CAMPAIGN_DETAILS_SAVE_FAIL](state, action) {
      return update(state, action.campaignId, {
        saving: false,
      })
    },

    [CAMPAIGN_LIST_DELETE](state, action) {
      return update(state, action.campaignId, {
        deleting: true,
      })
    },

    [CAMPAIGN_LIST_DELETE_SUCCESS](state, action) {
      const updatedEmailCampaigns = { ...state.emailCampaigns }
      delete updatedEmailCampaigns[action.campaignId]
      return { ...state, emailCampaigns: updatedEmailCampaigns }
    },

    [CAMPAIGN_LIST_DELETE_FAIL](state, action) {
      return update(state, action.campaignId, {
        deleting: false,
      })
    },

    [CAMPAIGN_BEGIN_SENDING](state, action) {
      return update(state, action.campaignId, {
        sending: true,
      })
    },

    [CAMPAIGN_BEGIN_SENDING_SUCCESS](state, action) {
      return update(state, action.campaignId, {
        sending: false,
      })
    },

    [CAMPAIGN_BEGIN_SENDING_FAIL](state, action) {
      return update(state, action.campaignId, {
        sending: false,
      })
    },

    [CAMPAIGN_STATS_LOAD](state, { campaignId }) {
      const currentStats =
        get(state, `emailCampaigns.${campaignId}.stats`, {}) ?? {}
      return update(state, campaignId, {
        stats: { ...currentStats, loading: true },
      })
    },

    [CAMPAIGN_STATS_LOAD_FAIL](state, { campaignId }) {
      const currentStats =
        get(state, `emailCampaigns.${campaignId}.stats`, {}) ?? {}
      return update(state, campaignId, {
        stats: { ...currentStats, loading: false },
      })
    },

    [CAMPAIGN_STATS_LOAD_SUCCESS](state, { campaignId, result, fullStats }) {
      return update(state, campaignId, {
        stats: { ...result, fullStats, loading: false },
      })
    },

    [CAMPAIGN_SCHEDULED_TO_SEND_SUCCESS](state, action) {
      return update(state, action.campaignId, { ...action.updatedCampaign })
    },

    [CAMPAIGN_RESCHEDULED_SUCCESS](state, action) {
      return update(state, action.campaignId, { ...action.updatedCampaign })
    },

    [CAMPAIGN_SCHEDULE_CANCELLED_SUCCESS](state, action) {
      return update(state, action.campaignId, {
        scheduled_at: null,
        scheduled_at_timezone: null,
        status: 'draft',
      })
    },
  },
)
